/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db } from './firebase';
import {
  doc, setDoc, getDoc, collection, query,
  where, getDocs, serverTimestamp, increment, updateDoc
} from 'firebase/firestore';

// ─────────────────────────────────────────────────────────────────────────────
// TimerLectura — temporizador de sesión de lectura
//
// Props:
//   userId    — UID de Firebase del usuario actual
//   libroId   — ID del libro (generarIdLibro)
//   libro     — objeto { titulo, autor, portada }
//   esPremium — boolean — controla si ve historial completo
//
// Colecciones Firestore que crea (no toca ninguna existente):
//   sesiones_lectura/{userId}_{libroId}_{timestamp}
//     userId, libroId, titulo, autor, segundos, fecha
//   stats_timer/{userId}
//     totalSegundos, totalSesiones  (incrementales)
//   stats_timer_libro/{userId}_{libroId}
//     userId, libroId, titulo, autor, totalSegundos, totalSesiones
// ─────────────────────────────────────────────────────────────────────────────

const SEGUNDOS_MIN_GUARDAR = 30; // sesiones menores de 30s no se guardan

function formatearTiempo(seg) {
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  const s = seg % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatearResumen(seg) {
  if (!seg || seg < 60) return `${seg || 0}s`;
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ── Guardar sesión en Firestore ───────────────────────────────────────────────
async function guardarSesion(userId, libroId, libro, segundos) {
  if (!userId || !libroId || segundos < SEGUNDOS_MIN_GUARDAR) return;
  const tsId = Date.now();
  try {
    // 1. Documento de la sesión individual
    await setDoc(doc(db, 'sesiones_lectura', `${userId}_${libroId}_${tsId}`), {
      userId,
      libroId,
      titulo: libro?.titulo || '',
      autor: libro?.autor || '',
      portada: libro?.portada || '',
      segundos,
      fecha: serverTimestamp(),
    });

    // 2. Acumular en stats globales del usuario
    await setDoc(doc(db, 'stats_timer', userId), {
      totalSegundos: increment(segundos),
      totalSesiones: increment(1),
    }, { merge: true });

    // 3. Acumular en stats por libro
    await setDoc(doc(db, 'stats_timer_libro', `${userId}_${libroId}`), {
      userId,
      libroId,
      titulo: libro?.titulo || '',
      autor: libro?.autor || '',
      portada: libro?.portada || '',
      totalSegundos: increment(segundos),
      totalSesiones: increment(1),
    }, { merge: true });

  } catch (e) {
    console.error('Error guardando sesión de lectura:', e);
  }
}

// ── Hook: cargar tiempo acumulado de este libro ───────────────────────────────
function useTiempoLibro(userId, libroId) {
  const [totalSegundos, setTotalSegundos] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    if (!userId || !libroId) { setCargando(false); return; }
    let activo = true;
    const cargar = async () => {
      try {
        const snap = await getDoc(doc(db, 'stats_timer_libro', `${userId}_${libroId}`));
        if (activo) setTotalSegundos(snap.exists() ? (snap.data().totalSegundos || 0) : 0);
      } catch {
        if (activo) setTotalSegundos(0);
      }
      if (activo) setCargando(false);
    };
    cargar();
    return () => { activo = false; };
  }, [userId, libroId]);

  return { totalSegundos, cargando, setTotalSegundos };
}

// ── Hook: historial de sesiones recientes ─────────────────────────────────────
function useSesionesRecientes(userId, libroId, esPremium, recargar) {
  const [sesiones, setSesiones] = useState([]);

  useEffect(() => {
    if (!userId || !libroId) return;
    let activo = true;
    const cargar = async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, 'sesiones_lectura'),
            where('userId', '==', userId),
            where('libroId', '==', libroId),
          )
        );
        const todas = snap.docs
          .map(d => d.data())
          .sort((a, b) => (b.fecha?.toMillis?.() || 0) - (a.fecha?.toMillis?.() || 0));
        // Gratis: solo última semana (7 días)
        const limite7d = Date.now() - 7 * 24 * 3600 * 1000;
        const filtradas = esPremium
          ? todas
          : todas.filter(s => (s.fecha?.toMillis?.() || 0) > limite7d);
        if (activo) setSesiones(filtradas.slice(0, esPremium ? 30 : 7));
      } catch { }
    };
    cargar();
    return () => { activo = false; };
  }, [userId, libroId, esPremium, recargar]);

  return sesiones;
}

// ── Componente principal ──────────────────────────────────────────────────────
export function TimerLectura({ userId, libroId, libro, esPremium, onUpgrade }) {
  const [activo, setActivo] = useState(false);
  const [segundosActuales, setSegundosActuales] = useState(0);
  const [guardando, setGuardando] = useState(false);
  const [ultimasSesionSegundos, setUltimaSesionSegundos] = useState(null);
  const [mostrarHistorial, setMostrarHistorial] = useState(false);
  const [recargar, setRecargar] = useState(0);

  const intervalRef = useRef(null);
  const segundosRef = useRef(0);

  const { totalSegundos, cargando, setTotalSegundos } = useTiempoLibro(userId, libroId);
  const sesiones = useSesionesRecientes(userId, libroId, esPremium, recargar);

  // Reiniciar timer al cambiar de libro
  useEffect(() => {
    detener(false);
    setSegundosActuales(0);
    segundosRef.current = 0;
    setUltimaSesionSegundos(null);
    setMostrarHistorial(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libroId]);

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const iniciar = () => {
    if (activo) return;
    setActivo(true);
    setUltimaSesionSegundos(null);
    intervalRef.current = setInterval(() => {
      segundosRef.current += 1;
      setSegundosActuales(s => s + 1);
    }, 1000);
  };

  const detener = useCallback(async (guardar = true) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setActivo(false);
    const seg = segundosRef.current;

    if (guardar && seg >= SEGUNDOS_MIN_GUARDAR) {
      setGuardando(true);
      await guardarSesion(userId, libroId, libro, seg);
      setGuardando(false);
      setUltimaSesionSegundos(seg);
      setTotalSegundos(prev => (prev || 0) + seg);
      setRecargar(r => r + 1);
    }

    setSegundosActuales(0);
    segundosRef.current = 0;
  }, [userId, libroId, libro, setTotalSegundos]);

  const tiempoTotal = (totalSegundos || 0) + (activo ? segundosActuales : 0);

  return (
    <div style={{
      background: '#111',
      border: '1px solid #1e1e1e',
      borderRadius: '14px',
      padding: '16px',
      margin: '14px 0',
    }}>
      {/* Cabecera */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#1DB954">
            <path d="M15 1H9v2h6V1zm-4 13h2V8h-2v6zm8.03-6.61l1.42-1.42c-.43-.51-.9-.99-1.41-1.41l-1.42 1.42C16.07 4.74 14.12 4 12 4c-4.97 0-9 4.03-9 9s4.02 9 9 9 9-4.03 9-9c0-2.12-.74-4.07-1.97-5.61zM12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
          </svg>
          <span style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold' }}>
            Timer de lectura
          </span>
        </div>

        {/* Tiempo total acumulado */}
        {!cargando && tiempoTotal > 0 && (
          <span style={{ fontSize: '12px', color: '#1DB954', fontWeight: 'bold' }}>
            {formatearResumen(tiempoTotal)} en este libro
          </span>
        )}
      </div>

      {/* Display del timer */}
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <div style={{
          fontSize: '46px',
          fontWeight: 900,
          letterSpacing: '-2px',
          color: activo ? 'white' : '#2a2a2a',
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1,
          marginBottom: '4px',
          transition: 'color 0.3s',
        }}>
          {formatearTiempo(segundosActuales)}
        </div>
        {activo && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
            <div style={{
              width: '7px', height: '7px', borderRadius: '50%',
              background: '#1DB954',
              boxShadow: '0 0 8px #1DB954',
              animation: 'pulse 1.5s infinite',
            }} />
            <span style={{ fontSize: '12px', color: '#1DB954', fontWeight: 'bold' }}>Sesión activa</span>
          </div>
        )}
        {!activo && segundosActuales === 0 && (
          <span style={{ fontSize: '12px', color: '#2a2a2a' }}>Pulsa para empezar</span>
        )}
      </div>

      {/* Botones */}
      <div style={{ display: 'flex', gap: '8px' }}>
        {!activo ? (
          <button
            onClick={iniciar}
            style={{
              flex: 1,
              background: '#1DB954',
              color: 'black',
              border: 'none',
              borderRadius: '25px',
              padding: '11px',
              fontWeight: 900,
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '7px',
              transition: '0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#1ed760'}
            onMouseLeave={e => e.currentTarget.style.background = '#1DB954'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            {guardando ? 'Guardando...' : 'Empezar sesión'}
          </button>
        ) : (
          <>
            <button
              onClick={() => detener(true)}
              disabled={guardando}
              style={{
                flex: 1,
                background: guardando ? '#1a1a1a' : '#f44336',
                color: guardando ? '#333' : 'white',
                border: 'none',
                borderRadius: '25px',
                padding: '11px',
                fontWeight: 900,
                cursor: guardando ? 'default' : 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '7px',
                transition: '0.2s',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h12v12H6z"/></svg>
              {guardando ? 'Guardando...' : 'Terminar'}
            </button>
            <button
              onClick={() => detener(false)}
              style={{
                background: 'transparent',
                color: '#444',
                border: '1px solid #2a2a2a',
                borderRadius: '25px',
                padding: '11px 14px',
                cursor: 'pointer',
                fontSize: '12px',
                transition: '0.2s',
              }}
              title="Cancelar sin guardar"
            >
              Cancelar
            </button>
          </>
        )}
      </div>

      {/* Confirmación sesión guardada */}
      {ultimasSesionSegundos !== null && !activo && (
        <div style={{
          marginTop: '12px',
          background: '#0d2a1a',
          border: '1px solid #1DB95433',
          borderRadius: '10px',
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span style={{ color: '#1DB954', fontSize: '16px' }}>✓</span>
          <span style={{ fontSize: '13px', color: '#1DB954', fontWeight: 'bold' }}>
            Sesión guardada — {formatearResumen(ultimasSesionSegundos)}
          </span>
        </div>
      )}

      {/* Aviso sesión muy corta */}
      {ultimasSesionSegundos === null && !activo && segundosActuales === 0 && sesiones.length === 0 && null}

      {/* ── Historial de sesiones ── */}
      {(sesiones.length > 0 || (!esPremium && totalSegundos > 0)) && (
        <div style={{ marginTop: '14px', borderTop: '1px solid #1a1a1a', paddingTop: '12px' }}>
          <button
            onClick={() => setMostrarHistorial(v => !v)}
            style={{
              background: 'none', border: 'none', color: '#555',
              cursor: 'pointer', fontSize: '12px', padding: 0,
              display: 'flex', alignItems: 'center', gap: '5px',
              width: '100%', justifyContent: 'space-between',
            }}
          >
            <span style={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Historial de sesiones
            </span>
            <span style={{ fontSize: '10px' }}>{mostrarHistorial ? '▲' : '▼'}</span>
          </button>

          {mostrarHistorial && (
            <div style={{ marginTop: '10px' }}>
              {/* Muro premium si no lo es */}
              {!esPremium && (
                <div style={{
                  background: 'linear-gradient(135deg, #1a0533, #1a1a2e)',
                  border: '1px solid #a78bfa33',
                  borderRadius: '10px',
                  padding: '12px 14px',
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                }}>
                  <div>
                    <p style={{ margin: '0 0 2px', fontSize: '12px', fontWeight: 'bold', color: 'white' }}>
                      ✦ Historial completo — Premium
                    </p>
                    <p style={{ margin: 0, fontSize: '11px', color: '#7c5fad' }}>
                      Ves la última semana. Premium guarda todo.
                    </p>
                  </div>
                  <button
                    onClick={onUpgrade}
                    style={{
                      background: 'linear-gradient(135deg, #6a3fa0, #a78bfa)',
                      color: 'white', border: 'none', borderRadius: '15px',
                      padding: '6px 12px', fontWeight: 'bold', cursor: 'pointer',
                      fontSize: '11px', flexShrink: 0,
                    }}
                  >
                    Ver más
                  </button>
                </div>
              )}

              {/* Lista de sesiones */}
              {sesiones.length === 0 ? (
                <p style={{ fontSize: '12px', color: '#333', margin: '8px 0 0' }}>
                  Aún no hay sesiones guardadas{!esPremium ? ' esta semana' : ''}.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {sesiones.map((s, i) => {
                    const fecha = s.fecha?.toDate?.();
                    const hoy = new Date();
                    const esHoy = fecha && fecha.toDateString() === hoy.toDateString();
                    const esAyer = fecha && new Date(fecha).toDateString() === new Date(hoy - 86400000).toDateString();
                    const labelFecha = esHoy ? 'Hoy'
                      : esAyer ? 'Ayer'
                      : fecha ? fecha.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : '';
                    return (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '7px 10px',
                          background: '#0a0a0a',
                          borderRadius: '8px',
                          border: '1px solid #1a1a1a',
                        }}
                      >
                        <span style={{ fontSize: '12px', color: '#555' }}>{labelFecha}</span>
                        <span style={{
                          fontSize: '13px',
                          fontWeight: 'bold',
                          color: s.segundos >= 600 ? '#1DB954' : '#aaa',
                        }}>
                          {formatearResumen(s.segundos)}
                        </span>
                      </div>
                    );
                  })}
                  {/* Resumen total del período */}
                  {sesiones.length > 1 && (
                    <div style={{
                      padding: '7px 10px',
                      background: '#141414',
                      borderRadius: '8px',
                      border: '1px solid #1DB95422',
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginTop: '2px',
                    }}>
                      <span style={{ fontSize: '11px', color: '#555', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {esPremium ? 'Total acumulado' : 'Total esta semana'}
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: 900, color: '#1DB954' }}>
                        {formatearResumen(sesiones.reduce((acc, s) => acc + (s.segundos || 0), 0))}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
